import { postRequest } from "lib/core/apiClient";
import { getAccessToken } from "./auth";
import { apiURI, constants, disableFingerprint, fingerprintPaths } from "./constants";

/**
 * Fingerprint data collected from the browser
 */
export interface FingerprintData {
  user_agent: string;
  platform: string;
  hardware_concurrency: number;
  device_memory: number;
  screen_resolution: string;
  device_pixel_ratio: number;
  color_depth: number;
  timezone_offset: number;
  languages: readonly string[] | string[];
  timezone: string;
  cookies_enabled: boolean;
  fonts_hash: string;
  canvas_hash: string;
  webgl_hash: string;
  audio_hash: string;
  // Graphics & Rendering
  webgl_renderer: string;
  webgl_vendor: string;
  webgl_extensions: string;
  webgl_params: string;
  // Hardware & Platform
  max_touch_points: number;
  touch_support: boolean;
  architecture: string;
  cpu_class: string;
  os_cpu: string;
  // Browser Identity
  vendor: string;
  vendor_flavors: string;
  plugin_count: number;
  pdf_viewer_enabled: boolean;
  do_not_track: string;
  // Screen & Display
  screen_frame: string;
  color_gamut: string;
  hdr_support: boolean;
  monochrome: boolean;
  // OS & Accessibility Preferences
  reduced_motion: boolean;
  dark_mode: boolean;
  forced_colors: boolean;
  contrast_preference: string;
  inverted_colors: boolean;
  reduced_transparency: boolean;
  // Storage Capabilities
  local_storage_enabled: boolean;
  session_storage_enabled: boolean;
  indexed_db_enabled: boolean;
  // Math & Locale
  math_fingerprint: string;
  date_time_locale: string;
  // Font Preferences
  font_preferences: string;
  extension_hash: string;
  permission_states: string;
  pointer_type: string;
}

/**
 * Signals the service stores in `signals` JSONB but does not encode yet - unknown keys are ignored
 * by the hash, the vector and the cache key, so collecting them is free until they earn a weight.
 * Optional because a browser that lacks the API sends nothing rather than a placeholder.
 */
export interface ExtendedSignals {
  store_domain: string;
  storage_quota: number;
  speech_voices: string;
  media_devices: string;
  intl_hour_cycle?: string;
  intl_calendar?: string;
  intl_numbering_system?: string;
  ua_model?: string;
  ua_platform_version?: string;
  ua_architecture?: string;
  ua_bitness?: string;
  ua_full_version_list?: string;
}

/** Everything one collection produces: the encoded parameters plus the stored-only ones. */
export type CollectedSignals = FingerprintData & ExtendedSignals;

/** The high-entropy half of UA-CH, which TypeScript's DOM lib does not declare. */
interface UserAgentData {
  getHighEntropyValues?: (hints: string[]) => Promise<{
    model?: string;
    platformVersion?: string;
    architecture?: string;
    bitness?: string;
    fullVersionList?: { brand: string; version: string }[];
  }>;
}

/**
 * Request body for both fingerprint endpoints — they take the same shape, which is what lets a caller
 * that already knows the person skip /resolve entirely.
 *
 * No user id: we send the token in Authorization and the gateway turns it into X-SHOPFLO-REQ-ID.
 */
interface FingerprintRequestBody {
  signals: CollectedSignals;
  /** map-user only: names the device we already resolved, so the service skips re-encoding it. */
  id?: string;
}

// Frozen: fonts_hash is pinned in the service's identity hash, so ANY change to this list - even
// removing the duplicate "Tahoma" below - re-buckets every device that has the font and re-mints it
// as new. It must also stay byte-identical to checkout-ui-script's copy, or one device lands in two
// buckets depending on the surface it was seen on. New font probes belong in a new key, not here.
const fontList: string[] = [
  // --- Browser UI / Browser-Specific ---
  "Fira Sans", // Firefox UI
  "Fira Mono",
  "Segoe UI", // Chrome & Windows UI
  "Segoe UI Symbol",
  "SF Pro Text", // Safari & macOS UI
  "SF Pro Display",
  "SF Mono", // Safari console font
  "Roboto", // Chrome/Android UI
  "Roboto Condensed",
  "Roboto Mono",
  "Noto Sans", // Chrome fallback for wide unicode
  "Noto Serif",
  "Noto Mono",
  "Ubuntu", // Ubuntu/Linux UI
  "Ubuntu Mono",
  "Cantarell", // GNOME default
  "Droid Sans", // Legacy Android
  "Droid Serif",
  "Droid Sans Mono",
  "Chrome OS", // Chrome OS font
  "Lucida Grande", // Legacy Safari/macOS
  "Lucida Sans Unicode",

  // --- Windows Core Fonts ---
  "Arial",
  "Arial Black",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
  "Times New Roman",
  "Georgia",
  "Impact",
  "Consolas",
  "Courier New",
  "Segoe Print",
  "Segoe Script",
  "Calibri",
  "Cambria",
  "Corbel",
  "Candara",
  "Constantia",
  "Comic Sans MS",

  // --- macOS Fonts ---
  "Helvetica",
  "Helvetica Neue",
  "Avenir",
  "Avenir Next",
  "Gill Sans",
  "Optima",
  "Menlo",
  "Monaco",
  "Palatino",
  "Skia",
  "Apple Chancery",
  "American Typewriter",
  "Hoefler Text",
  "Marker Felt",
  "Noteworthy",
  "Times",
  "Geneva",

  // --- iOS Fonts (Safari Mobile) ---
  "San Francisco",
  "New York",
  "SF Compact",
  "SF Arabic",
  "PingFang SC",
  "PingFang TC",
  "PingFang HK",
  "Heiti SC",
  "Heiti TC",

  // --- Android Fonts ---
  "Roboto Slab",
  "Roboto Flex",
  "Roboto Serif",
  "Product Sans",
  "Google Sans",
  "Open Sans",
  "Open Sans Condensed",

  // --- Linux Fonts ---
  "DejaVu Sans",
  "DejaVu Serif",
  "DejaVu Sans Mono",
  "Liberation Sans",
  "Liberation Serif",
  "Liberation Mono",
  "Red Hat Display",
  "Red Hat Text",
  "FreeSans",
  "FreeSerif",
  "FreeMono",

  // --- CJK Fonts ---
  "MS Gothic",
  "MS Mincho",
  "Meiryo",
  "Yu Gothic",
  "Yu Mincho",
  "SimSun",
  "SimHei",
  "Microsoft YaHei",
  "Microsoft JhengHei",
  "PMingLiU",
  "MingLiU",

  // --- Special Unicode Fonts ---
  "Symbol",
  "Wingdings",
  "Wingdings 2",
  "Wingdings 3",
  "Webdings",

  // --- Developer / Console Fonts ---
  "JetBrains Mono",
  "Source Code Pro",
  "Source Sans Pro",
  "Source Serif Pro",
  "Inconsolata",
  "Hack",
  "Cascadia Code",
  "Cascadia Mono",
  "Fira Code",

  // --- Decorative / Misc ---
  "Brush Script MT",
  "Century Gothic",
  "Franklin Gothic Medium",
  "Garamond",
  "Bodoni 72",
  "Didot",
  "Charter",
  "Copperplate",
  "Rockwell",
  "Baskerville",
  "Big Caslon",
  "Chalkboard",
  "Chalkboard SE",
  "Chalkduster",
  "Courier",
  "Tahoma",
  "Lato",
  "Montserrat",
  "Poppins",
  "Raleway",
  "Playfair Display",
  "Gotham",
  "Avenir Next Condensed",

  // --- Display / Design Fonts ---
  "Papyrus",
  "Stencil",
  "Trattatello",
  "Herculanum",
  "Party LET",
  "Jazz LET",
  "Snell Roundhand",
  "Zapfino",
  "Copperplate Gothic",
  "Gill Sans MT",

  // --- Rare system fonts (adds entropy) ---
  "Andale Mono",
  "Batang",
  "Gulim",
  "Kailasa",
  "Kohinoor Devanagari",
  "Mukta",
  "Lohit Devanagari",
  "Kokila",
  "Shree Devanagari 714",
  "Nirmala UI",
  "Kartika",
];

/**
 * Checks if a font is available
 * @param font - The font to check
 * @returns True if the font is available, false otherwise
 */
const isFontAvailable = (font: string): boolean => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return false;
  }

  const text = "abcdefghijklmnopqrstuvwxyz0123456789";

  context.font = "72px monospace";
  const baselineSize = context.measureText(text).width;

  context.font = `72px '${font}', monospace`;
  const newSize = context.measureText(text).width;

  return baselineSize !== newSize;
};

/**
 * Generates a simple hash for fingerprint components
 * @param type - Type of hash to generate
 * @returns Generated hash
 */
const generateSimpleHash = (type: string): string => {
  const data = `${type}-${navigator.userAgent}-${window.screen.width}-${window.screen.height}`;
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
};

/**
 * Generates a hash from available fonts
 * @returns The hash of available fonts
 */
const getFontsHash = async (): Promise<string> => {
  try {
    const availableFonts: string[] = [];

    // Check which fonts are available
    for (const font of fontList) {
      if (isFontAvailable(font)) {
        availableFonts.push(font);
      }
    }

    // Create a string from available fonts
    const fontsString = availableFonts.join(",");

    // Hash the fonts string using SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(fontsString);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b: number) => b.toString(16).padStart(2, "0")).join("");
  } catch (error) {
    console.error("Error generating fonts hash:", error);
    // Fallback to simple hash if crypto API fails
    return generateSimpleHash("fonts");
  }
};

/**
 * Generates a hash for the canvas
 * @returns The hash of the canvas
 */
export const getCanvasHash = async (): Promise<string> => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return generateSimpleHash("canvas");
  }

  canvas.width = 200;
  canvas.height = 50;

  // Draw text + shapes (anything deterministic)
  ctx.textBaseline = "top";
  ctx.font = "16px Arial";
  ctx.fillStyle = "#f60";
  ctx.fillRect(0, 0, 200, 50);
  ctx.fillStyle = "#069";
  ctx.fillText("Canvas Fingerprint Test", 10, 10);

  // Convert canvas to data URL
  const dataURL = canvas.toDataURL();

  // Hash the dataURL using SubtleCrypto (SHA-256)
  const encoder = new TextEncoder();
  const data = encoder.encode(dataURL);

  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  const hashHex = hashArray.map((b: number) => b.toString(16).padStart(2, "0")).join("");

  return hashHex;
};

/**
 * Generates a hash for the audio
 * @returns The hash of the audio
 */
const getAudioHash = async (): Promise<string> => {
  const ctx = new OfflineAudioContext(1, 44100, 44100);

  const oscillator = ctx.createOscillator();
  oscillator.type = "triangle";
  oscillator.frequency.value = 1000;

  const compressor = ctx.createDynamicsCompressor();

  oscillator.connect(compressor);
  compressor.connect(ctx.destination);

  oscillator.start(0);

  const buffer = await ctx.startRendering();
  const samples = buffer.getChannelData(0);

  // Mid-buffer at full precision. The first 100 samples sit under the compressor's attack and
  // toFixed(3) collapsed them to near-identical strings on most devices - the digest separated
  // almost nobody. Changing this moves audio_hash for every device; it carries 0.002 of vector
  // weight and no place in the identity hash, so the move is priced in. Must stay identical to
  // checkout-ui-script's copy, or one device measures differently per surface.
  let str = "";
  for (let i = 4500; i < 4700; i++) {
    str += samples[i];
  }

  // Hash the string using SHA-256
  const encoder = new TextEncoder();
  const data = encoder.encode(str);

  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b: number) => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Generates a hash for the WebGL info
 * @param gl - The shared context
 * @returns The hash of the WebGL info
 */
const getWebGLInfoHash = async (gl: WebGLRenderingContext | null): Promise<string> => {
  if (!gl) return "nowebgl";

  const info = [
    gl.getParameter(gl.VENDOR),
    gl.getParameter(gl.RENDERER),
    gl.getParameter(gl.VERSION),
    gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
    JSON.stringify(gl.getSupportedExtensions()),
  ].join("::");

  const encoder = new TextEncoder();
  const data = encoder.encode(info);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b: number) => b.toString(16).padStart(2, "0")).join("");
};

// ============================================================
// Graphics & Rendering
// ============================================================

/**
 * One context for every WebGL read. Each getter used to create its own, six per collection, and
 * browsers cap live contexts (~16) - the readings are context-independent, so sharing changes no
 * collected value.
 */
const createWebglContext = (): WebGLRenderingContext | null => {
  try {
    const canvas = document.createElement("canvas");

    return (
      (canvas.getContext("webgl") as WebGLRenderingContext | null) ||
      (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null)
    );
  } catch {
    return null;
  }
};

const releaseWebglContext = (gl: WebGLRenderingContext | null): void => {
  try {
    (gl?.getExtension("WEBGL_lose_context") as { loseContext?: () => void } | null)?.loseContext?.();
  } catch {
    // best effort - the context dies with the detached canvas anyway
  }
};

const getWebglRenderer = (gl: WebGLRenderingContext | null): string => {
  try {
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "";
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "";
  } catch {
    return "";
  }
};

const getWebglVendor = (gl: WebGLRenderingContext | null): string => {
  try {
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return "";
    return gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "";
  } catch {
    return "";
  }
};

const getWebglExtensions = (gl: WebGLRenderingContext | null): string => {
  try {
    if (!gl) return "";
    const extensions = gl.getSupportedExtensions();
    if (!extensions) return "";
    return extensions.slice().sort().join(",");
  } catch {
    return "";
  }
};

const getWebglParams = (gl: WebGLRenderingContext | null): string => {
  try {
    if (!gl) return "";

    const params: (number | undefined)[] = [
      gl.getParameter(gl.MAX_TEXTURE_SIZE),
      gl.getParameter(gl.MAX_RENDERBUFFER_SIZE),
      gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.[0],
      gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.[1],
      gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      gl.getParameter(gl.MAX_VARYING_VECTORS),
      gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
      gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
      gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
    ];

    const shaderTypes = [gl.VERTEX_SHADER, gl.FRAGMENT_SHADER];
    const precisionTypes = [gl.LOW_FLOAT, gl.MEDIUM_FLOAT, gl.HIGH_FLOAT];
    for (const shader of shaderTypes) {
      for (const precision of precisionTypes) {
        const fmt = gl.getShaderPrecisionFormat(shader, precision);
        if (fmt) {
          params.push(fmt.rangeMin, fmt.rangeMax, fmt.precision);
        }
      }
    }

    return params.join(",");
  } catch {
    return "";
  }
};

// ============================================================
// Hardware & Platform
// ============================================================

const getMaxTouchPoints = (): number => {
  try {
    return navigator.maxTouchPoints || 0;
  } catch {
    return 0;
  }
};

const getTouchSupport = (): boolean => {
  try {
    const hasTouchStart = "ontouchstart" in window;
    let canCreateTouch = false;
    try {
      document.createEvent("TouchEvent");
      canCreateTouch = true;
    } catch {
      // not supported
    }
    return hasTouchStart || canCreateTouch;
  } catch {
    return false;
  }
};

const getArchitecture = (): string => {
  try {
    const buffer = new ArrayBuffer(4);
    const floatView = new Float32Array(buffer);
    const byteView = new Uint8Array(buffer);

    floatView[0] = Infinity;
    floatView[0] = floatView[0] - floatView[0]; // NaN

    const nanBytes = Array.from(byteView).join(",");

    if (byteView[2] === 192 && (byteView[3] === 127 || byteView[3] === 255)) {
      return "x86";
    }
    return nanBytes;
  } catch {
    return "unknown";
  }
};

const getCpuClass = (): string => {
  try {
    return (navigator as Navigator & { cpuClass?: string }).cpuClass || "";
  } catch {
    return "";
  }
};

const getOsCpu = (): string => {
  try {
    return (navigator as Navigator & { oscpu?: string }).oscpu || "";
  } catch {
    return "";
  }
};

// ============================================================
// Browser Identity
// ============================================================

const getVendor = (): string => {
  try {
    return navigator.vendor || "";
  } catch {
    return "";
  }
};

const getVendorFlavors = (): string => {
  const checks: [string, () => boolean][] = [
    ["chrome", () => "chrome" in window],
    ["safari", () => "safari" in window],
    ["webkit", () => "__webkit" in window || "WebKitCSSMatrix" in window],
    ["firefox", () => "__firefox__" in window || "InstallTrigger" in window],
    ["edge", () => "__edgeTrackingPreventionStatistics" in window],
    ["opera", () => "opr" in window || "opera" in window],
    ["samsung", () => "samsungAr" in window],
    ["ucbrowser", () => "ucweb" in window || "UCShellJava" in window],
    ["yandex", () => "yandex" in window || "__yb" in window],
    ["puffin", () => "puffinDevice" in window],
    ["crWeb", () => "__crWeb" in window || "__gCrWeb" in window],
  ];

  const detected: string[] = [];
  for (const [name, check] of checks) {
    try {
      if (check()) detected.push(name);
    } catch {
      // skip
    }
  }
  return detected.join(",");
};

const getPluginCount = (): number => {
  try {
    return navigator.plugins?.length || 0;
  } catch {
    return 0;
  }
};

const getPdfViewerEnabled = (): boolean => {
  try {
    return (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled ?? false;
  } catch {
    return false;
  }
};

const getDoNotTrack = (): string => {
  try {
    return navigator.doNotTrack || (window as Window & { doNotTrack?: string }).doNotTrack || "unspecified";
  } catch {
    return "unspecified";
  }
};

// ============================================================
// Screen & Display
// ============================================================

const getScreenFrame = (): string => {
  try {
    const s = window.screen;
    const top = (s as Screen & { availTop?: number }).availTop || 0;
    const left = (s as Screen & { availLeft?: number }).availLeft || 0;
    const right = s.width - s.availWidth - left;
    const bottom = s.height - s.availHeight - top;
    return `${top},${right},${bottom},${left}`;
  } catch {
    return "";
  }
};

const getColorGamut = (): string => {
  try {
    if (window.matchMedia("(color-gamut: rec2020)").matches) return "rec2020";
    if (window.matchMedia("(color-gamut: p3)").matches) return "p3";
    if (window.matchMedia("(color-gamut: srgb)").matches) return "srgb";
    return "";
  } catch {
    return "";
  }
};

const getHdrSupport = (): boolean => {
  try {
    return window.matchMedia("(dynamic-range: high)").matches;
  } catch {
    return false;
  }
};

const getMonochrome = (): boolean => {
  try {
    return window.matchMedia("(monochrome)").matches;
  } catch {
    return false;
  }
};

// ============================================================
// OS & Accessibility Preferences
// ============================================================

const getReducedMotion = (): boolean => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

const getDarkMode = (): boolean => {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
};

const getForcedColors = (): boolean => {
  try {
    return window.matchMedia("(forced-colors: active)").matches;
  } catch {
    return false;
  }
};

const getContrastPreference = (): string => {
  try {
    if (window.matchMedia("(prefers-contrast: more)").matches) return "more";
    if (window.matchMedia("(prefers-contrast: less)").matches) return "less";
    if (window.matchMedia("(prefers-contrast: forced)").matches) return "forced";
    return "none";
  } catch {
    return "none";
  }
};

const getInvertedColors = (): boolean => {
  try {
    return window.matchMedia("(inverted-colors: inverted)").matches;
  } catch {
    return false;
  }
};

const getReducedTransparency = (): boolean => {
  try {
    return window.matchMedia("(prefers-reduced-transparency: reduce)").matches;
  } catch {
    return false;
  }
};

// ============================================================
// Storage Capabilities
// ============================================================

const getLocalStorageEnabled = (): boolean => {
  try {
    const key = "__fp_test__";
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const getSessionStorageEnabled = (): boolean => {
  try {
    const key = "__fp_test__";
    sessionStorage.setItem(key, "1");
    sessionStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

const getIndexedDbEnabled = (): boolean => {
  try {
    return !!window.indexedDB;
  } catch {
    return false;
  }
};

// ============================================================
// Math & Locale
// ============================================================

const getMathFingerprint = (): string => {
  try {
    const results = [
      Math.acos(0.123456789),
      Math.asin(0.123456789),
      Math.atan(0.123456789),
      Math.atan2(0.123456789, 0.987654321),
      Math.sin(0.123456789),
      Math.cos(0.123456789),
      Math.tan(0.123456789),
      Math.acosh(1.123456789),
      Math.asinh(0.123456789),
      Math.atanh(0.123456789),
      Math.sinh(0.123456789),
      Math.cosh(0.123456789),
      Math.tanh(0.123456789),
      Math.exp(0.123456789),
      Math.expm1(0.123456789),
      Math.log1p(0.123456789),
    ];
    return results.join(",");
  } catch {
    return "";
  }
};

const getDateTimeLocale = (): string => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().locale || "";
  } catch {
    return "";
  }
};

// ============================================================
// Font Preferences
// ============================================================

const getFontPreferences = (): string => {
  try {
    const testString = "mmMwWLliI0fifl";
    const testSize = "72px";
    const families = ["default", "serif", "sans-serif", "monospace", "system-ui", "cursive", "fantasy"];

    const span = document.createElement("span");
    span.style.position = "absolute";
    span.style.left = "-9999px";
    span.style.top = "-9999px";
    span.style.fontSize = testSize;
    span.style.lineHeight = "normal";
    span.textContent = testString;
    document.body.appendChild(span);

    const widths = families.map((family) => {
      span.style.fontFamily = family === "default" ? "" : family;
      return span.getBoundingClientRect().width;
    });

    document.body.removeChild(span);
    return widths.join(",");
  } catch {
    return "";
  }
};

const PERMISSIONS = [
  "geolocation",
  "notifications",
  "camera",
  "microphone",
  "midi",
  "background-sync",
  "persistent-storage",
  "accelerometer",
  "gyroscope",
  "magnetometer",
  "clipboard-read",
  "clipboard-write",
  "payment-handler",
  "screen-wake-lock",
  "storage-access",
  "local-fonts",
];

/**
 * A token list, not a digest: the service SimHashes this, so agreeing on most permissions must
 * measure closer than agreeing on none. Queried in parallel — sixteen sequential awaits on the
 * checkout critical path is latency for nothing. Origin-scoped, so a storefront and the checkout
 * iframe answer differently for one device.
 */
const getPermissionStates = async (): Promise<string> => {
  if (!navigator.permissions?.query) {
    return "";
  }

  const states = await Promise.all(
    PERMISSIONS.map(async (name) => {
      try {
        const status = await navigator.permissions.query({ name: name as PermissionName });

        return `${name}:${status.state}`;
      } catch {
        return null;
      }
    }),
  );

  return states.filter(Boolean).join(",");
};

/**
 * Extensions inferred from the artefacts they inject. A token list for the same SimHash reason as
 * permissions; the `_hash` in the parameter name is historical, nothing is hashed here.
 */
const getExtensionTokens = (): string => {
  const probes: [string, () => boolean][] = [
    ["grammarly", () => Boolean(document.querySelector("grammarly-extension,[data-grammarly-shadow-root]"))],
    ["lastpass", () => Boolean(document.querySelector("[data-lastpass-icon-root],#lp-pom-root"))],
    ["bitwarden", () => Boolean(document.querySelector("[data-bw-elem]"))],
    ["honey", () => Boolean(document.querySelector("#honeyContainer,[id^=honey-]"))],
    ["metamask", () => Boolean((window as any).ethereum?.isMetaMask)],
    ["coinbase", () => Boolean((window as any).ethereum?.isCoinbaseWallet)],
    ["phantom", () => Boolean((window as any).solana?.isPhantom)],
    ["darkreader", () => Boolean(document.querySelector("style.darkreader"))],
    ["adblock", () => isAdBlocked()],
  ];

  const found: string[] = [];
  for (const [name, probe] of probes) {
    try {
      if (probe()) found.push(name);
    } catch {
      // A broken probe costs one token; a thrown error costs the whole payload.
    }
  }

  return found.join(",");
};

/** Bait an ad blocker will hide. Off-screen and removed immediately. */
const isAdBlocked = (): boolean => {
  const bait = document.createElement("div");
  bait.className = "adsbox pub_300x250 ad-placement";
  bait.style.cssText = "position:absolute;left:-9999px;height:10px;";
  document.body.appendChild(bait);

  const blocked = bait.offsetHeight === 0;
  document.body.removeChild(bait);

  return blocked;
};

const getPointerType = (): string => {
  try {
    if (window.matchMedia("(pointer: coarse)").matches) return "coarse";
    if (window.matchMedia("(pointer: fine)").matches) return "fine";

    return "none";
  } catch {
    return "";
  }
};

// ============================================================
// Extended signals - stored by the service, not yet encoded
// ============================================================

/**
 * Which storefront this collection came from. Stored, never encoded: at any weight that could
 * separate two stores it would give one device a different identity per storefront, and at a weight
 * too small to do that it does nothing for matching - the two goals are one knob turned opposite
 * ways. Its value is diagnostic: a device seen across dozens of stores in a day, carrying hundreds of
 * users, is a collapsed identity, and that is only visible with this recorded.
 *
 * <p>This app runs on the Shopflo origin rather than the merchant's, so the storefront is the
 * referrer; location.host is the fallback and names checkout itself, which is still true and simply
 * less useful.
 */
const getStoreDomain = (): string => {
  try {
    if (document.referrer) {
      return new URL(document.referrer).host;
    }

    return window.location.host || "";
  } catch {
    return "";
  }
};

/** Quota derives from free disk - per-person state on identical hardware, which is exactly what the
 * iPhone population lacks. Slow-moving, so it belongs in the vector and never in the identity hash. */
const getStorageQuota = async (): Promise<number> => {
  try {
    const { quota } = await navigator.storage.estimate();

    return quota || 0;
  } catch {
    return 0;
  }
};

/** Installed voices track language packs and downloaded voices - per-person, works on iOS. A token
 * list, not a digest, for the same SimHash reason as permissions. Chrome loads the list async and
 * fires voiceschanged; the timeout keeps a quiet browser from stalling the payload. */
const getSpeechVoices = async (): Promise<string> => {
  try {
    if (!window.speechSynthesis) return "";
    let voices = speechSynthesis.getVoices();
    if (!voices.length) {
      voices = await new Promise<SpeechSynthesisVoice[]>((resolve) => {
        const timer = setTimeout(() => resolve(speechSynthesis.getVoices()), 150);
        speechSynthesis.addEventListener(
          "voiceschanged",
          () => {
            clearTimeout(timer);
            resolve(speechSynthesis.getVoices());
          },
          { once: true },
        );
      });
    }

    return voices
      .map((voice) => `${voice.name}:${voice.lang}`)
      .sort()
      .join(",");
  } catch {
    return "";
  }
};

/** UA-CH high-entropy values. On Android Chrome, model names the literal device ("moto g45") - the
 * discriminator the family keys lack. Chromium-only; absent elsewhere, as any missing signal is. */
const getUaClientHints = async (): Promise<Partial<ExtendedSignals>> => {
  try {
    const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
    if (!uaData?.getHighEntropyValues) return {};

    const hints = await uaData.getHighEntropyValues([
      "model",
      "platformVersion",
      "architecture",
      "bitness",
      "fullVersionList",
    ]);

    return {
      ua_model: hints.model || "",
      ua_platform_version: hints.platformVersion || "",
      ua_architecture: hints.architecture || "",
      ua_bitness: hints.bitness || "",
      ua_full_version_list: (hints.fullVersionList || [])
        .map((entry) => `${entry.brand}:${entry.version}`)
        .join(","),
    };
  } catch {
    return {};
  }
};

/** 12/24-hour clock, calendar and numbering system are user settings, not model facts. */
const getIntlSettings = (): Partial<ExtendedSignals> => {
  try {
    const options = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions();

    return {
      intl_hour_cycle: options.hourCycle || "",
      intl_calendar: options.calendar || "",
      intl_numbering_system: options.numberingSystem || "",
    };
  } catch {
    return {};
  }
};

/** Kind counts only - ids are origin-scoped random without permission and labels are empty. */
const getMediaDeviceKinds = async (): Promise<string> => {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return "";
    const devices = await navigator.mediaDevices.enumerateDevices();
    const kinds: Record<string, number> = {};
    for (const device of devices) {
      kinds[device.kind] = (kinds[device.kind] || 0) + 1;
    }

    return Object.keys(kinds)
      .sort()
      .map((kind) => `${kind}:${kinds[kind]}`)
      .join(",");
  } catch {
    return "";
  }
};

/**
 * Signals the service stores but does not yet encode: unknown keys ride into the signals JSONB
 * untouched, so shipping collection first builds the labelled corpus that decides each candidate's
 * weight before any encoding change. Nothing here can move signal_hash, the vector or the cache key.
 *
 * <p><b>Both collectors must send these before any of them is given a registry weight.</b> The
 * collector that does not send one encodes it as 0, which is a systematic vector gap between the
 * storefront and this app for one physical device - enough to split every device in two.
 */
const collectExtendedSignals = async (): Promise<ExtendedSignals> => {
  const [storageQuota, speechVoices, uaHints, mediaDevices] = await Promise.all([
    getStorageQuota(),
    getSpeechVoices(),
    getUaClientHints(),
    getMediaDeviceKinds(),
  ]);

  return {
    store_domain: getStoreDomain(),
    storage_quota: storageQuota,
    speech_voices: speechVoices,
    media_devices: mediaDevices,
    ...getIntlSettings(),
    ...uaHints,
  };
};

/**
 * Collects browser fingerprint data
 * @returns Browser fingerprint data
 */
export const collectFingerprintData = async (): Promise<CollectedSignals | null> => {
  const gl = createWebglContext();
  try {
    // The digests are independent, and four sequential awaits on the checkout critical path was
    // latency for nothing - the same reasoning permissions already followed.
    const [fontsHash, canvasHash, webglHash, audioHash, permissionStates, extendedSignals] =
      await Promise.all([
        getFontsHash(),
        getCanvasHash(),
        getWebGLInfoHash(gl),
        getAudioHash(),
        getPermissionStates(),
        collectExtendedSignals(),
      ]);

    const fingerprintData: CollectedSignals = {
      user_agent: navigator.userAgent || "",
      platform: navigator.platform || "",
      hardware_concurrency: navigator.hardwareConcurrency || 0,
      device_memory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      device_pixel_ratio: window.devicePixelRatio || 1,
      color_depth: window.screen.colorDepth || 24,
      timezone_offset: new Date().getTimezoneOffset(),
      languages: navigator.languages || [navigator.language] || [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      cookies_enabled: navigator.cookieEnabled || false,
      fonts_hash: fontsHash,
      canvas_hash: canvasHash,
      webgl_hash: webglHash,
      audio_hash: audioHash,
      // Graphics & Rendering
      webgl_renderer: getWebglRenderer(gl),
      webgl_vendor: getWebglVendor(gl),
      webgl_extensions: getWebglExtensions(gl),
      webgl_params: getWebglParams(gl),
      // Hardware & Platform
      max_touch_points: getMaxTouchPoints(),
      touch_support: getTouchSupport(),
      architecture: getArchitecture(),
      cpu_class: getCpuClass(),
      os_cpu: getOsCpu(),
      // Browser Identity
      vendor: getVendor(),
      vendor_flavors: getVendorFlavors(),
      plugin_count: getPluginCount(),
      pdf_viewer_enabled: getPdfViewerEnabled(),
      do_not_track: getDoNotTrack(),
      // Screen & Display
      screen_frame: getScreenFrame(),
      color_gamut: getColorGamut(),
      hdr_support: getHdrSupport(),
      monochrome: getMonochrome(),
      // OS & Accessibility Preferences
      reduced_motion: getReducedMotion(),
      dark_mode: getDarkMode(),
      forced_colors: getForcedColors(),
      contrast_preference: getContrastPreference(),
      inverted_colors: getInvertedColors(),
      reduced_transparency: getReducedTransparency(),
      // Storage Capabilities
      local_storage_enabled: getLocalStorageEnabled(),
      session_storage_enabled: getSessionStorageEnabled(),
      indexed_db_enabled: getIndexedDbEnabled(),
      // Math & Locale
      math_fingerprint: getMathFingerprint(),
      date_time_locale: getDateTimeLocale(),
      // Font Preferences
      font_preferences: getFontPreferences(),
      extension_hash: getExtensionTokens(),
      permission_states: permissionStates,
      pointer_type: getPointerType(),
      ...extendedSignals,
    };

    return fingerprintData;
  } catch (error) {
    console.error("Error collecting fingerprint data:", error);
    return null;
  } finally {
    releaseWebglContext(gl);
  }
};

/** The expiry and the marker go with the ID - alone they describe an ID we no longer hold. */
export const clearAnonymousId = (): void => {
  localStorage.removeItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID);
  localStorage.removeItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID_EXPIRY);
  localStorage.removeItem(constants.LOCAL_STORAGE_FLO_ANON_ID_MAPPED);
};

/** A missing expiry means an ID written before this existed - stale, not forever. */
export const readAnonymousId = (): string | null => {
  const id = localStorage.getItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID);
  const expiry = localStorage.getItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID_EXPIRY);
  if (id && expiry && Date.now() < Number(expiry)) return id;

  clearAnonymousId();
  return null;
};

/** Sweeps keys stranded under earlier versions, which nothing reads any more. */
const clearStaleAnonymousIdKeys = (): void => {
  const current = [
    constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID,
    constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID_EXPIRY,
    constants.LOCAL_STORAGE_FLO_ANON_ID_MAPPED,
  ];
  const prefix = constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID.replace(/-v\d+$/, "");
  Object.keys(localStorage)
    .filter((key) => key.startsWith(prefix) && !current.includes(key))
    .forEach((key) => localStorage.removeItem(key));
};

export const writeAnonymousId = (id: string): void => {
  clearStaleAnonymousIdKeys();
  const expiresAt = Date.now() + constants.FLO_ANONYMOUS_ID_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID, id);
  localStorage.setItem(constants.LOCAL_STORAGE_FLO_ANONYMOUS_ID_EXPIRY, String(expiresAt));
};

/**
 * Fetches the device id from fingerprint-service. Both endpoints take the same body, so a known
 * shopper goes straight to /map-user and is resolved and linked in one call.
 */
export const fetchAnonymousId = async (
  isSigned: boolean,
  authToken?: string,
): Promise<{ fingerprintId?: string; mapped: boolean }> => {
  if (disableFingerprint) {
    return { mapped: false };
  }

  try {
    const fingerprintData = await collectFingerprintData();
    if (!fingerprintData) {
      return { mapped: false };
    }

    // isSigned means the shopper logged in, not that we still hold a token; without one map-user has
    // nothing to authenticate.
    const token = authToken ?? getAccessToken();
    const isIdentified = isSigned && Boolean(token);

    const requestBody: FingerprintRequestBody = { signals: fingerprintData };

    const existingId = readAnonymousId();
    if (isIdentified && existingId) {
      requestBody.id = existingId;
    }

    const response = await postRequest(
      isIdentified ? fingerprintPaths.MAP_USER : fingerprintPaths.RESOLVE,
      requestBody,
      apiURI.FINGERPRINT,
      token ? { Authorization: token } : undefined,
    );

    if (response?.id) {
      // mapped means the service WROTE the link, not that map-user was called: a device the crowd
      // guard refused answers mapped=false, and marking it mapped here would stop the next login
      // from ever retrying. Older service builds omit the field; treat absent as written so the
      // marker keeps its old behaviour until the field ships.
      return { fingerprintId: response.id, mapped: isIdentified && response.mapped !== false };
    }
  } catch (error) {
    console.error("Error fetching anonymous ID:", error);
  }

  return { mapped: false };
};
